import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Code2, GitBranch, Server, Sparkles, Loader2, Activity, TrendingDown,
  CheckCircle2, ExternalLink, Layers, FileCode2, ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import PublicNavbar from '../components/PublicNavbar';
import FooterBackground from '../components/FooterBackground';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public "Live Build" page at /build
 * ----------------------------------
 * Transparency dashboard for prospects, investors, and engineering brand —
 * mirrors the admin module inspector but redacted for public consumption.
 *
 * Pulls /api/build/stats (no auth, 60s cached). Shows server.py shrink %,
 * total endpoint count, total LOC, and a CI-passing badge.
 */
const BuildPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/build/stats`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative" data-testid="public-build-page">
      <PublicNavbar />
      <div className="relative">
        <FooterBackground />
        <div className="relative max-w-6xl mx-auto px-4 pt-16 pb-20">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex flex-wrap justify-center items-center gap-2 mb-4">
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest">
                ENGINEERING TRANSPARENCY
              </Badge>
              <Link
                to="/build/momentum"
                data-testid="shipping-weekly-pill"
                className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-400/50 text-[11px] font-bold tracking-widest text-cyan-300 hover:text-cyan-200 transition-all"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-cyan-400" />
                </span>
                SHIPPING WEEKLY
                <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
              Built in the open.{' '}
              <span className="bg-clip-text text-transparent bg-emerald-400 bg-gradient-to-r from-emerald-400 to-cyan-400">
                Refactored daily.
              </span>
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto text-lg">
              We don't hide our build process. Every commit reduces our monolith,
              every deploy adds a contract test. Here's the live state.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-emerald-300" />
            </div>
          )}

          {error && !loading && (
            <Card className="bg-rose-500/10 border-rose-500/30 max-w-lg mx-auto">
              <CardContent className="p-5 text-rose-200 text-sm">Could not load stats: {error}</CardContent>
            </Card>
          )}

          {data && !loading && (
            <>
              {/* CI / Tests badge strip */}
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                <BadgePill icon={<CheckCircle2 className="w-3.5 h-3.5" />} label="CI" value="passing" tone="emerald" />
                <BadgePill icon={<Activity className="w-3.5 h-3.5" />} label="Tests" value="75 passing" tone="emerald" />
                <BadgePill icon={<Layers className="w-3.5 h-3.5" />} label="Coverage" value="multi-tenant + plan-gating + billing" tone="cyan" />
                <BadgePill icon={<GitBranch className="w-3.5 h-3.5" />} label="Refactor" value={`${data.monolith.reduction_pct}% modularized`} tone="purple" />
              </div>

              {/* Hero progress card */}
              <Card className="bg-gradient-to-br from-emerald-500/[0.08] to-cyan-500/[0.06] border-emerald-500/30 shadow-2xl shadow-emerald-500/10 mb-8" data-testid="monolith-progress-card">
                <CardContent className="p-7">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="w-5 h-5 text-emerald-300" />
                        <span className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Monolith → Modular</span>
                      </div>
                      <h2 className="text-2xl font-bold mb-2">Breaking up the 7,940-line monolith</h2>
                      <p className="text-slate-300 text-sm mb-5 max-w-lg">
                        We're decomposing our backend into focused router files — one per domain. Each extraction
                        ships with contract tests so we never silently lose an endpoint.
                      </p>
                      <div className="grid grid-cols-3 gap-4 max-w-lg">
                        <Stat label="Lines reduced" value={data.monolith.lines_reduced.toLocaleString()} accent="emerald" />
                        <Stat label="Reduction %" value={`${data.monolith.reduction_pct}%`} accent="emerald" />
                        <Stat label="Current LOC" value={data.monolith.current_lines.toLocaleString()} accent="slate" />
                      </div>
                    </div>
                    <div className="w-full lg:w-1/3 lg:max-w-xs">
                      <div className="text-xs text-slate-300 mb-2 flex justify-between">
                        <span>Modularization progress</span>
                        <span className="text-emerald-300 font-bold">{data.monolith.reduction_pct}%</span>
                      </div>
                      <Progress value={data.monolith.reduction_pct} className="h-3 bg-white/5" />
                      <div className="text-xs text-slate-300 mt-2">
                        {data.monolith.starting_lines.toLocaleString()} → {data.monolith.current_lines.toLocaleString()} lines in server.py
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footprint stats grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <FootprintCard
                  icon={<Server className="w-6 h-6" />}
                  iconBg="bg-emerald-500/10 text-cyan-400"
                  label="API endpoints"
                  primary={data.footprint.total_endpoints}
                  secondary={`${data.modularization.endpoints_in_routes} in modular routes · ${data.monolith.endpoints_remaining} in monolith`}
                />
                <FootprintCard
                  icon={<FileCode2 className="w-6 h-6" />}
                  iconBg="bg-emerald-500/10 text-emerald-300"
                  label="Router files"
                  primary={data.modularization.router_files}
                  secondary={`${data.modularization.extracted_from_monolith} extracted from monolith`}
                />
                <FootprintCard
                  icon={<Code2 className="w-6 h-6" />}
                  iconBg="bg-emerald-500/10 text-cyan-400"
                  label="Python lines"
                  primary={data.footprint.total_python_lines.toLocaleString()}
                  secondary={`${data.modularization.lines_in_routes.toLocaleString()} in routes/, ${data.monolith.current_lines.toLocaleString()} in monolith`}
                />
              </div>

              {/* Engineering principles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-emerald-300" />
                      <span className="font-bold text-white">Multi-tenant by default</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Every authenticated endpoint routes through a per-org physical
                      collection. There is no shared <code className="text-cyan-300 text-xs">db.costs</code> —
                      a coding mistake leaks zero rows, surfaces immediately.
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                      <span className="font-bold text-white">Plan gating at the route level</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Pro features are gated by a single <code className="text-cyan-300 text-xs">require_paid_plan</code> dependency.
                      Free / expired-trial users get a structured 402 with the upgrade URL — never a stack trace.
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <GitBranch className="w-4 h-4 text-cyan-400" />
                      <span className="font-bold text-white">Refactor with contract tests</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Each extracted module ships with a contract test that hits its endpoints
                      via the same URL the frontend uses — catches "duplicate handler silently
                      wins" regressions cheaply.
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="font-bold text-white">CI on every push</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Lint + tenancy regression + frontend build smoke + pricing-catalog
                      contract test. Six minutes from commit to greenlight.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Footer CTA */}
              <div className="text-center">
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    onClick={() => window.location.href = '/pricing'}
                    size="lg"
                    className="bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 h-12"
                    data-testid="build-page-cta-btn"
                  >
                    See pricing
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/build/momentum'}
                    size="lg"
                    variant="outline"
                    className="border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 text-cyan-200 hover:text-cyan-100 px-8 h-12"
                    data-testid="build-page-momentum-btn"
                  >
                    See weekly momentum
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="text-xs text-slate-300 mt-3">
                  Stats updated every 60 seconds · Pulled from <code className="text-slate-300">/api/build/stats</code>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BadgePill = ({ icon, label, value, tone }) => {
  const palettes = {
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    cyan: 'bg-emerald-500/10 text-cyan-300 border-emerald-500/30',
    purple: 'bg-emerald-500/10 text-cyan-300 border-emerald-500/30',
  };
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${palettes[tone] || palettes.emerald}`}
    >
      {icon}
      <span className="text-white/60">{label}:</span>
      <span>{value}</span>
    </div>
  );
};

const Stat = ({ label, value, accent }) => {
  const colors = {
    emerald: 'text-emerald-300',
    slate: 'text-white',
  };
  return (
    <div>
      <div className={`text-2xl font-bold ${colors[accent] || colors.slate}`}>{value}</div>
      <div className="text-xs text-slate-300 mt-0.5">{label}</div>
    </div>
  );
};

const FootprintCard = ({ icon, iconBg, label, primary, secondary }) => (
  <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
    <CardContent className="p-5">
      <div className={`inline-flex p-3 rounded-xl ${iconBg} mb-3`}>{icon}</div>
      <div className="text-xs uppercase tracking-widest text-slate-300 mb-1">{label}</div>
      <div className="text-3xl font-bold text-white">{primary}</div>
      <div className="text-xs text-slate-300 mt-1">{secondary}</div>
    </CardContent>
  </Card>
);

export default BuildPage;
